import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Grid,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "./FAQ.module.css";
import { Helmet } from "react-helmet";

const FAQ = () => {
  const faqData = [
    {
      question: "What is AI Super Resolution?",
      answer:
        "AI Super Resolution is a technology that uses artificial intelligence to enhance the resolution and quality of images. It can upscale low-resolution images, sharpen details, and improve overall clarity while preserving the original content.",
    },
    {
      question: "What types of images can I enhance with this AI?",
      answer:
        "You can enhance a wide range of images, including photos, digital art, scanned documents, and more. The AI works best with images that have some level of detail to begin with.",
    },
    {
      question: "How does the AI improve image resolution?",
      answer:
        "The AI uses advanced algorithms to analyze and reconstruct image details. It intelligently upscales the image while reducing noise and artifacts, resulting in a sharper and clearer high-resolution version.",
    },
    {
      question: "Can I customize the enhancement process?",
      answer:
        "Yes, you can adjust settings such as the level of upscaling, sharpness, and noise reduction to achieve the desired results. The AI also provides automatic optimization for ease of use.",
    },
    {
      question: "How long does it take to enhance an image?",
      answer:
        "Enhancement time typically ranges from 10 to 30 seconds, depending on the image size, resolution, and the level of upscaling required.",
    },
    {
      question: "What file formats are supported for upload and download?",
      answer:
        "We support common image formats like JPEG, PNG, and TIFF for uploads. Enhanced images can be downloaded in high-resolution formats, including JPEG and PNG.",
    },
    {
      question: "Can I use enhanced images for commercial purposes?",
      answer:
        "Yes, all images enhanced with our platform are royalty-free and can be used for personal or commercial projects, including printing, marketing, and digital media.",
    },
    {
      question: "Does the AI work on heavily compressed or low-quality images?",
      answer:
        "Yes, the AI can significantly improve heavily compressed or low-quality images by reconstructing details and reducing artifacts. However, results may vary depending on the original image quality.",
    },
    {
      question: "Is my data safe when using AI Super Resolution?",
      answer:
        "Absolutely. We prioritize user privacy and data security. Uploaded images are processed securely and are not stored on our servers after the enhancement is complete.",
    },
    {
      question: "What resolutions are supported for enhanced images?",
      answer:
        "We support resolutions from standard sizes (e.g., 512x512) up to 8K UHD (7680x4320) and beyond. Higher resolutions may require premium credits or subscriptions.",
    },
    {
      question: "Can I enhance multiple images at once?",
      answer:
        "Yes, our platform supports batch processing, allowing you to upload and enhance multiple images simultaneously for faster results.",
    },
  ];
  // Create schema markup for FAQ
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Helmet>
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Typography variant="h2" className={styles.heading}>
        Frequently Asked Questions
      </Typography>
      <Typography variant="h6" className={styles.subheading}>
        Can't find the answer you're looking for? Reach out to our customer
        support team
      </Typography>

      <Grid item xs={12} md={8}>
        {faqData.map((faq, index) => (
          <Accordion key={index} className={styles.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Typography className={styles.question}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={styles.answer}>{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
    </Container>
  );
};

export default FAQ;
